import { PropsWithChildren } from "react";
import { Sparkles, AlertTriangle, ShieldCheck } from "lucide-react";
import { useMarketplace } from "../../marketplace/context";
import Spinner from "../ui/Spinner";

// Renders children only when an Account has been loaded for the URL's
// accountId. While loading, shows a centered spinner. If the session can't be
// verified, prompts the user to open the app from their CRM; if the account
// doesn't exist, shows an install prompt.
const AccountGate = ({ children }: PropsWithChildren) => {
  const { loading, notFound, unauthenticated, account } = useMarketplace();

  if (loading) {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center">
        <Spinner size={28} />
      </div>
    );
  }

  if (unauthenticated) {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center p-6">
        <div className="relative w-full max-w-md bg-white border border-ink-200/70 rounded-2xl shadow-card overflow-hidden text-center px-8 py-10">
          <div className="absolute inset-0 bg-mesh-1 opacity-60 pointer-events-none" />
          <div className="relative">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-100 to-violet-100 text-brand-700 flex items-center justify-center mx-auto mb-4">
              <ShieldCheck size={22} />
            </div>
            <h1 className="font-display text-lg font-bold text-ink-900 tracking-tight">
              Open Smart Links from your CRM
            </h1>
            <p className="text-sm text-ink-500 mt-1.5 leading-relaxed">
              We couldn't verify your session. Smart Links runs inside your CRM —
              please open it from there (Apps → Smart Links) to continue. Opening
              it directly isn't supported for your security.
            </p>
            <div className="mt-5 inline-flex items-center gap-1.5 text-[11px] font-medium text-ink-500 bg-ink-50 ring-1 ring-ink-200 rounded-md px-2 py-1">
              <Sparkles size={11} className="text-brand-500" />
              <span>Smart Links · secure session</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !account) {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center p-6">
        <div className="relative w-full max-w-md bg-white border border-ink-200/70 rounded-2xl shadow-card overflow-hidden text-center px-8 py-10">
          <div className="absolute inset-0 bg-mesh-1 opacity-60 pointer-events-none" />
          <div className="relative">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 text-amber-700 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={22} />
            </div>
            <h1 className="font-display text-lg font-bold text-ink-900 tracking-tight">
              App not installed
            </h1>
            <p className="text-sm text-ink-500 mt-1.5 leading-relaxed">
              We couldn't find an account for this URL. Install Smart Links from the
              marketplace to get started.
            </p>
            <div className="mt-5 inline-flex items-center gap-1.5 text-[11px] font-medium text-ink-500 bg-ink-50 ring-1 ring-ink-200 rounded-md px-2 py-1">
              <Sparkles size={11} className="text-brand-500" />
              <span>Smart Links · marketplace</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AccountGate;
